import { EQUAL_WEIGHTS_TITLE } from '@upright/common/model/data';
import { getValueSetName } from '@upright/ui/untidy/redux/selectors';
import { Bounds, CarouselLayout } from '@upright/ui/common/upright/core/impact-lens-profile/common/layout/types';
import { toBounds } from '@upright/ui/common/upright/core/impact-lens-profile/common/layout/utils';
import { useSelector } from 'react-redux';
import { useCarouselLayout } from '../../../impact-lens-profile/context';

interface Layout {
  equalWeightsResultBounds: () => Bounds;
  customValueSetResultBounds: () => Bounds;
  impactSummaryAndRankBounds: () => Bounds & { narrowMode: boolean };
  actionsBounds: () => Bounds & { narrowMode: boolean };
}

function isNarrowMode(width: number) {
  return width < 800;
}

const scoreRowHeight = 72;
const narrowScoreRowHeight = 150; // OH: Score row requires more height in narrow mode to fit impact summaries stacked on top of each other
const narrowReleaseAndControlsHeight = 65;
const narrowLegendHeight = 10;
export function aggregateNetImpactResultAreaHeight(width: number, numberOfScores: number) {
  const narrowMode = isNarrowMode(width);
  if (narrowMode) {
    return numberOfScores * narrowScoreRowHeight + narrowReleaseAndControlsHeight + narrowLegendHeight;
  } else {
    return numberOfScores * scoreRowHeight;
  }
}

export function aggregateResultAreaLayout(baseLayout: CarouselLayout, valueSet: string): Layout {
  const area = baseLayout.aggregateResultArea();
  const numberOfScores = valueSet === EQUAL_WEIGHTS_TITLE ? 1 : 2;
  const narrowMode = isNarrowMode(baseLayout.fullArea().width);

  const height = narrowMode ? numberOfScores * narrowScoreRowHeight : area.height;

  function equalWeightsResultBounds() {
    const x1 = area.x1;
    const x2 = area.x2;
    const y1 = area.y1;
    const y2 = y1 + height;
    return toBounds({ x1, x2, y1, y2 });
  }

  function customValueSetResultBounds() {
    const ewBounds = equalWeightsResultBounds();
    const x1 = ewBounds.x1;
    const x2 = ewBounds.x2;
    const y1 = ewBounds.y2;
    const y2 = y1 + scoreRowHeight;
    return toBounds({ x1, x2, y1, y2 });
  }

  function impactSummaryAndRankBounds() {
    const centerArea = baseLayout.centerArea();

    function getBounds() {
      if (narrowMode) {
        const narrowImpactSummaryAndRankWidth = 260;
        const refArea = numberOfScores === 1 ? equalWeightsResultBounds() : customValueSetResultBounds();
        return {
          x1: area.x1,
          x2: area.x1 + narrowImpactSummaryAndRankWidth,
          y1: area.y1,
          y2: refArea.y2,
        };
      } else {
        return {
          x1: area.x1,
          x2: centerArea.x1,
          y1: area.y1,
          y2: area.y2,
        };
      }
    }

    return {
      narrowMode,
      ...toBounds(getBounds()),
    };
  }

  function actionsBounds() {
    const centerArea = baseLayout.centerArea();

    function getBounds() {
      if (narrowMode) {
        const refArea = numberOfScores === 1 ? equalWeightsResultBounds() : customValueSetResultBounds();
        return {
          x1: area.x1,
          x2: area.x2,
          y1: refArea.y2 + narrowReleaseAndControlsHeight,
          y2: area.y2,
        };
      } else {
        return {
          x1: centerArea.x2 + 130, // ToDo: dynamic based on NIR legend width
          x2: area.x2,
          y1: area.y1,
          y2: area.y2,
        };
      }
    }

    return {
      narrowMode,
      ...toBounds(getBounds()),
    };
  }

  return {
    equalWeightsResultBounds,
    customValueSetResultBounds,
    impactSummaryAndRankBounds,
    actionsBounds,
  };
}

export function useAggregateResultAreaLayout() {
  return aggregateResultAreaLayout(useCarouselLayout(), useSelector(getValueSetName));
}
